import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { api, errMsg, formatPrice } from "../lib/api";
import { Sparkles, Lock, ShieldCheck } from "lucide-react";
import HolographicPlaceholder from "../components/HolographicPlaceholder";
import { toast } from "sonner";
import { PaymentForm, CreditCard } from "react-square-web-payments-sdk";

export default function Checkout() {
  const { items, subtotalCents, clear } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();

  const [config, setConfig] = useState(null);
  useEffect(() => {
    api.get("/config").then(({ data }) => setConfig(data)).catch(() => setConfig({ square_enabled: false }));
  }, []);

  const [form, setForm] = useState({
    email: user?.email || "",
    name: user?.name || "",
    address_line1: "",
    city: "",
    postal_code: "",
    country: "GB",
  });
  const [redeem, setRedeem] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const userPoints = user?.loyalty_points || 0;
  const maxRedeemableByPoints = userPoints;
  const maxRedeemableByCart = Math.floor(subtotalCents / 5);
  const redeemMax = Math.min(maxRedeemableByPoints, maxRedeemableByCart);

  const discountCents = redeem * 5;
  const shippingCents = subtotalCents >= 5000 ? 0 : 499;
  const taxCents = Math.floor((subtotalCents - discountCents) * 0.08);
  const totalCents = subtotalCents - discountCents + shippingCents + taxCents;

  const validateAddress = () => {
    if (!form.email || !form.name || !form.address_line1 || !form.city || !form.postal_code) {
      setError("Please fill in all contact and shipping fields before paying");
      return false;
    }
    return true;
  };

  const placeOrder = async (sourceId, last4) => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/checkout", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        email: form.email,
        name: form.name,
        address_line1: form.address_line1,
        city: form.city,
        postal_code: form.postal_code,
        country: form.country,
        redeem_points: redeem,
        payment_method: "card",
        card_last4: last4 || null,
        source_id: sourceId || null,
      });
      clear();
      toast.success("Order confirmed!");
      try { sessionStorage.setItem(`kavo_order_${data.id}`, form.email); } catch {}
      nav(`/order/${data.id}`);
    } catch (err) {
      setError(errMsg(err, "Checkout failed"));
    } finally {
      setBusy(false);
    }
  };

  const onMockPay = async (e) => {
    e?.preventDefault?.();
    if (!validateAddress()) return;
    await placeOrder(null, "0000");
  };

  if (items.length === 0) {
    return (
      <div className="max-w-[800px] mx-auto px-6 py-20 text-center">
        <h1 className="font-display text-4xl text-white">Your cart is empty</h1>
        <p className="mt-3 text-white/60">Add some chase pulls before checking out.</p>
        <button onClick={() => nav("/shop")} data-testid="empty-cart-shop-btn" className="btn-violet mt-8 h-12 px-7 rounded-md">
          Browse the vault
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-[1200px] mx-auto px-6 py-12">
      <h1 className="font-display font-extrabold text-4xl lg:text-5xl tracking-tighter text-white">Checkout</h1>
      <p className="mt-2 text-sm text-white/50">{user ? "Authenticated checkout" : "Guest checkout"} • Secured by Square</p>

      <div className="mt-12 grid grid-cols-12 gap-10">
        {/* LEFT — fields */}
        <div className="col-span-12 lg:col-span-7 space-y-10">
          <Section title="Contact">
            <Field label="Email">
              <input type="email" required value={form.email} onChange={set("email")} data-testid="checkout-email" className={inputCls} />
            </Field>
            <Field label="Full name">
              <input required value={form.name} onChange={set("name")} data-testid="checkout-name" className={inputCls} />
            </Field>
          </Section>

          <Section title="Shipping address">
            <Field label="Address">
              <input required value={form.address_line1} onChange={set("address_line1")} data-testid="checkout-address" className={inputCls} />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="City">
                <input required value={form.city} onChange={set("city")} data-testid="checkout-city" className={inputCls} />
              </Field>
              <Field label="Postcode">
                <input required value={form.postal_code} onChange={set("postal_code")} data-testid="checkout-postal" className={inputCls} />
              </Field>
            </div>
            <Field label="Country">
              <input required value={form.country} onChange={set("country")} data-testid="checkout-country" className={inputCls} />
            </Field>
          </Section>

          {user && (
            <Section title="Kavo Points" hint={<span className="text-[#B026FF] font-mono">{userPoints} pts available</span>}>
              <div className="rounded-lg border border-[#B026FF]/30 bg-[#B026FF]/5 p-5">
                <div className="flex items-center gap-2 text-sm text-white">
                  <Sparkles size={14} className="text-[#B026FF]" />
                  Redeem points for instant credit. <span className="font-mono ml-auto text-[#B026FF]">100 pts = £5.00</span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={redeemMax}
                  step={10}
                  value={redeem}
                  onChange={(e) => setRedeem(parseInt(e.target.value || 0, 10))}
                  data-testid="checkout-redeem-slider"
                  className="w-full mt-4 accent-[#B026FF]"
                />
                <div className="mt-2 flex items-center justify-between text-sm">
                  <span className="text-white/60">Redeem <span className="font-mono text-white">{redeem}</span> points</span>
                  <span className="font-mono text-[#B026FF]">- {formatPrice(redeem * 5)}</span>
                </div>
              </div>
            </Section>
          )}

          <Section
            title="Payment"
            hint={
              <span className="flex items-center gap-1.5">
                <Lock size={11} /> Square — {config?.square_environment === "production" ? "Live" : "Sandbox"}
              </span>
            }
          >
            {!config && <div className="text-sm text-white/50">Loading payment form…</div>}

            {config && config.square_enabled && (
              <div className="rounded-lg border border-white/10 bg-[#110E1D] p-5">
                <div data-testid="square-payment-form">
                  <PaymentForm
                    applicationId={config.square_application_id}
                    locationId={config.square_location_id}
                    cardTokenizeResponseReceived={async (token) => {
                      if (!validateAddress()) return;
                      if (!token?.token) {
                        setError("Card details could not be processed");
                        return;
                      }
                      const last4 = token?.details?.card?.last4 || null;
                      await placeOrder(token.token, last4);
                    }}
                  >
                    <CreditCard
                      buttonProps={{
                        css: {
                          backgroundColor: "#7A28CB",
                          fontSize: "14px",
                          color: "#fff",
                          letterSpacing: "0.04em",
                          fontWeight: 600,
                          height: "48px",
                          borderRadius: "8px",
                          "&:hover": { backgroundColor: "#9D4CDD" },
                          "&:disabled": { opacity: 0.5 },
                        },
                      }}
                    >
                      {busy ? "Processing…" : `Pay ${formatPrice(totalCents)}`}
                    </CreditCard>
                  </PaymentForm>
                </div>
                <div className="mt-3 text-xs text-white/40 leading-relaxed">
                  Sandbox test cards: <span className="font-mono text-white/60">4111 1111 1111 1111</span> · CVV any · any future date · postcode any.
                </div>
              </div>
            )}

            {config && !config.square_enabled && (
              <div className="rounded-lg border border-yellow-500/30 bg-yellow-500/10 p-5">
                <div className="text-sm text-yellow-100">
                  Square keys not configured — using mock checkout (no real charge).
                </div>
                <button
                  onClick={onMockPay}
                  disabled={busy}
                  data-testid="mock-pay-btn"
                  className="mt-4 btn-violet w-full h-12 rounded-md disabled:opacity-50"
                >
                  {busy ? "Processing…" : `Place mock order — ${formatPrice(totalCents)}`}
                </button>
              </div>
            )}
          </Section>
        </div>

        {/* RIGHT — summary */}
        <aside className="col-span-12 lg:col-span-5">
          <div className="card-elev rounded-xl p-6 sticky top-32">
            <h3 className="font-display text-xl text-white">Order summary</h3>

            <ul className="mt-5 space-y-4 max-h-[260px] overflow-y-auto pr-1">
              {items.map((it) => (
                <li key={it.product_id} className="flex gap-3 items-start">
                  <div className="w-14 shrink-0">
                    <HolographicPlaceholder label={it.name} ratio="aspect-[3/4]" imageUrl={it.image_url} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white truncate">{it.name}</div>
                    <div className="text-xs text-white/50 font-mono">× {it.quantity}</div>
                  </div>
                  <div className="font-mono text-sm text-white">{formatPrice(it.price_cents * it.quantity)}</div>
                </li>
              ))}
            </ul>

            <div className="mt-6 space-y-2 text-sm border-t border-white/5 pt-5">
              <Row label="Subtotal" value={formatPrice(subtotalCents)} />
              {discountCents > 0 && <Row label={`Kavo Points (${redeem})`} value={`- ${formatPrice(discountCents)}`} accent />}
              <Row label="Shipping" value={shippingCents === 0 ? "Free" : formatPrice(shippingCents)} />
              <Row label="Tax (8%)" value={formatPrice(taxCents)} />
              <div className="flex items-center justify-between text-lg pt-3 border-t border-white/5 mt-3">
                <span className="text-white">Total</span>
                <span className="font-mono text-white">{formatPrice(totalCents)}</span>
              </div>
            </div>

            {error && (
              <div data-testid="checkout-error" className="mt-4 text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2">
                {error}
              </div>
            )}

            <div className="mt-6 flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-white/40 font-mono justify-center">
              <ShieldCheck size={11} className="text-[#B026FF]" /> Encrypted • PCI Compliant
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

const inputCls =
  "w-full bg-[#110E1D] border border-white/10 rounded-md px-4 py-3 text-sm text-white focus:border-[#B026FF]/60 outline-none";

function Section({ title, hint, children }) {
  return (
    <section>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-xl text-white tracking-tight">{title}</h2>
        {hint && <div className="text-[11px] tracking-[0.22em] uppercase text-white/50 font-mono">{hint}</div>}
      </div>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="block text-[11px] tracking-[0.22em] uppercase text-white/50 font-bold mb-2">{label}</div>
      {children}
    </label>
  );
}

function Row({ label, value, accent }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-white/60">{label}</span>
      <span className={`font-mono ${accent ? "text-[#B026FF]" : "text-white"}`}>{value}</span>
    </div>
  );
}
